import React, { useState, useCallback } from 'react';
import { formRowSx } from '../utils/panelSurfaces';
import { buildStrategy1Payload } from '../utils/strategyPayloadBuilders';
import { useInitialConfig, mergeInto } from '../utils/useInitialConfig';
import PropTypes from 'prop-types';
import { useChartOverlays } from '../context/ChartOverlayContext';
import { addAreaHighlightOverlaysAndMessages } from './addAreaHighlightOverlaysUtil';
import { formatMessagesPanelTabs } from '../utils/messagesPanelUtils';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,

  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import HelpIcon from './HelpIcon';
import DeleteIcon from '@mui/icons-material/Delete';

// Placeholder lists (should be replaced with actual data/constants)
const PLANETS = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu'];


// Renders a summary string for a condition object based on its type
function renderConditionSummary(cond) {
  if (!cond || typeof cond !== 'object') return '';
  switch (cond.type) {
    case 1:
    case 'harmonic':
      return `Harmonic ${cond.harmonic} of ${cond.planet} (offset ${cond.offset}, ${cond.percent}%): value ${cond.value}, otherwise ${cond.otherwise}`;
    case 2:
    case 'angle2p':
      return `Angle between ${cond.planet1 || cond.planet} and ${cond.planet2} in degrees ${cond.from} to ${cond.to}: value ${cond.value}, otherwise ${cond.otherwise}`;
    case 3:
    case 'r2d':
      return `Retrograde to direct: Planet ${cond.planet}, value ${cond.conditionalValue ?? cond.value}, otherwise ${cond.defaultValue ?? cond.otherwise}`;
    case 4:
    case 'd2r':
      return `Direct to retrograde: Planet ${cond.planet}, value ${cond.conditionalValue ?? cond.value}, otherwise ${cond.defaultValue ?? cond.otherwise}`;
    case 5:
      return `Midpoint: [${cond.fromPlanet}/${cond.fromMid1}/${cond.fromMid2}] to [${cond.toPlanet}/${cond.toMid1}/${cond.toMid2}]: value ${cond.value}, otherwise ${cond.otherwise}`;
    default:
      return 'Unknown condition';
  }
}

// Helper to get max allowed angle between specific planet pairs
function getMaxAllowedAngle(p1, p2) {
  const pair = [p1, p2];
  if (pair.includes('Sun') && pair.includes('Venus')) return 48;
  if (pair.includes('Sun') && pair.includes('Mercury')) return 28;
  return 360;
}

/**
 * The top-level prefill keys this dialog's applier reads.
 *
 * <p>⚠ EXPORTED SO AN UNKNOWN FIELD CAN BE REFUSED RATHER THAN SILENTLY DROPPED. A prefill
 * naming a field that does not exist would otherwise open the dialog, change nothing and report
 * success — which for a Copilot is the "a control that lies" failure, in the one place the
 * caller has no way to notice it. Same precedent as `ctsDirection`, which refuses an unknown
 * name with the valid set listed rather than quietly running the default.
 *
 * <p>Nested keys are deliberately NOT listed: `mergeInto` already ignores sub-keys it does not
 * know, and enumerating them here would be a second declaration of every form's shape.
 *
 * <p>`prefillFields.test.js` reads the applier and fails if the two drift apart.
 */
export const PREFILL_FIELDS = Object.freeze([
  'conditions', 'scoreThreshold', 'highlightColor', 'form1', 'form3', 'form4', 'form5',
  'form6',
]);

export default function StrategyDesignDialog({ open, onClose, onRun, barData, extendedBarData, onJournalAppend, initialConfig = null }) {
  const { api, isAdmin } = useAuth();
  // State for each condition type
  const [conditions, setConditions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);

  // State for each condition form (one per section)
  const [form1, setForm1] = useState({ planet: 'Sun', offset: '0', harmonic: '30', percent: '50', value: '1', otherwise: '0' });

  const [form3, setForm3] = useState({ planet: 'Mercury', value: '1', otherwise: '0' });
  const [form4, setForm4] = useState({ planet: 'Mercury', value: '1', otherwise: '0' });
  const [form5, setForm5] = useState({ fromPlanet: 'Sun', fromMid1: 'Venus', fromMid2: 'Mars', toPlanet: 'Sun', toMid1: 'Mars', toMid2: 'Jupiter', value: '1', otherwise: '0' });
  // New: State for angle-between-planets study (type 6)
  const [form6, setForm6] = useState({ planet1: 'Sun', planet2: 'Jupiter', from: '0', to: '90', value: '1', otherwise: '0' });

  // Highlight options (must match allowed dropdown values!)
  const [highlightColor, setHighlightColor] = useState('rgba(33,150,243,0.7)');
  // Score threshold for area highlight
  const [scoreThreshold, setScoreThreshold] = useState('2.0');

  // Validation helpers
  const isFormFilled = (form, keys) => keys.every(k => form[k] !== '' && form[k] !== undefined);

  // Track last added form state for each study type
  const [lastAddedForm1, setLastAddedForm1] = useState(form1);
  const [lastAddedForm3, setLastAddedForm3] = useState(form3);
  const [lastAddedForm4, setLastAddedForm4] = useState(form4);
  const [lastAddedForm6, setLastAddedForm6] = useState(form6);

  /**
   * PREFILL. Applied once per distinct config object -- see useInitialConfig for why identity
   * rather than a deep compare, and why null does not reset.
   *
   * <p>`conditions` is the one field a Copilot most wants to set, and it REPLACES rather than
   * merging: a condition list is the study, so appending to whatever was on screen would run a
   * strategy nobody described. Every form merges, so naming one field leaves the rest alone.
   */
  useInitialConfig(initialConfig, (cfg) => {
    if (Array.isArray(cfg.conditions)) setConditions(cfg.conditions);
    if (cfg.scoreThreshold !== undefined) setScoreThreshold(String(cfg.scoreThreshold));
    if (cfg.highlightColor !== undefined) setHighlightColor(cfg.highlightColor);
    mergeInto(setForm1, cfg.form1, ['planet', 'offset', 'harmonic', 'percent', 'value', 'otherwise']);
    mergeInto(setForm3, cfg.form3, ['planet', 'value', 'otherwise']);
    mergeInto(setForm4, cfg.form4, ['planet', 'value', 'otherwise']);
    mergeInto(setForm5, cfg.form5,
      ['fromPlanet', 'fromMid1', 'fromMid2', 'toPlanet', 'toMid1', 'toMid2', 'value', 'otherwise']);
    mergeInto(setForm6, cfg.form6, ['planet1', 'planet2', 'from', 'to', 'value', 'otherwise']);
  });

  // Utility to compare forms
  const isFormChanged = (form, lastForm, keys) => keys.some(k => form[k] !== lastForm[k]);

  // Add condition handlers
  const handleAddCondition = (type) => {
    let form, keys, studyObj;
    switch (type) {
      case 1:
        form = form1;
        keys = ['planet', 'offset', 'harmonic', 'percent', 'value', 'otherwise'];
        if (!isFormFilled(form, keys)) return;
        studyObj = {
          type: 'harmonic',
          ...form,
          harmonic: Number(form.harmonic),
          offset: Number(form.offset),
          percentHarmonic: Number(form.percent),
          defaultValue: Number(form.otherwise),
          conditionalValue: Number(form.value)
        };
        console.log('[StrategyDesignDialog] Harmonic study object to backend:', studyObj);
        break;
      case 2: {
        form = form6;
        keys = ['planet1', 'planet2', 'from', 'to', 'value', 'otherwise'];
        // Check for max angle validity
        const maxAngle = getMaxAllowedAngle(form.planet1, form.planet2);
        if (Number(form.to) > maxAngle) return; // Prevent adding if invalid

        if (!isFormFilled(form, keys)) return;
        studyObj = {
          type: 'angle2p',
          ...form,
          planet: form.planet1,
          planet2: form.planet2,
          from: Number(form.from),
          to: Number(form.to),
          conditionalValue: Number(form.value),
          defaultValue: Number(form.otherwise)
        };
        break;
      }
      case 3:
        form = form3;
        keys = ['planet', 'value', 'otherwise'];
        if (!isFormFilled(form, keys)) return;
        studyObj = {
          type: 'r2d',
          ...form,
          defaultValue: Number(form.otherwise),
          conditionalValue: Number(form.value)
        };
        console.log('[StrategyDesignDialog] Retrograde to Direct study object to backend:', studyObj);
        break;
      case 4:
        form = form4;
        keys = ['planet', 'value', 'otherwise'];
        if (!isFormFilled(form, keys)) return;
        studyObj = {
          type: 'd2r',
          ...form,
          defaultValue: Number(form.otherwise),
          conditionalValue: Number(form.value)
        };
        break;
      case 5:
        form = form5;
        keys = ['fromPlanet', 'fromMid1', 'fromMid2', 'toPlanet', 'toMid1', 'toMid2', 'value', 'otherwise'];
        if (!isFormFilled(form, keys)) return;
        studyObj = {
          type,
          ...form,
          planet4: PLANETS.indexOf(form.toMid1),
          planet5: PLANETS.indexOf(form.toMid2)
        };
        break;
      default:
        return;
    }
    setConditions([...conditions, studyObj]);
  };
  
  // Delete handler
  const handleDelete = (idx) => {
    setConditions(conditions.filter((_, i) => i !== idx));
    if (selectedIndex === idx) setSelectedIndex(null);
  };

  // RUN handler for Strategy Design Study
  const { addOverlay } = useChartOverlays();
  const handleRun = useCallback(async () => {

    // Diagnostic: Log input state
    console.log('[StrategyDesignDialog] RUN clicked', { conditions, scoreThreshold, highlightColor });
    try {
      // Built by the SHARED builder since 2026-10-24, so a headless `strategy.run` and this
      // button send the same request -- two assemblies would agree on the day they were written
      // and differ from the first edit onward, and the difference would be a successful answer
      // to a different question.
      const payload = buildStrategy1Payload(
        { studies: conditions, scoreThreshold, highlightColor },
        { barData, extendedBarData },
      );
      console.log('[StrategyDesignDialog] Calling /api/strategy-design with payload:', payload);
      const response = await api.post('/api/strategy-design', payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const result = response.data;
      console.log('[StrategyDesignDialog] Backend response:', result);
      // Defensive: ensure arrays for downstream handlers
      const datePairs = Array.isArray(result.datePairs) ? result.datePairs : [];
      const summaryLines = Array.isArray(result.summaryLines) ? result.summaryLines : [];
      const futureDates = Array.isArray(result.futureDates) ? result.futureDates : [];

      // PROPER FIX: Convert futureDates from [{startDate, endDate}] to ["start-to-end"]
      // This ensures the shared utility receives the data format it expects.
      const formattedFutureDates = futureDates.map(pair => `${pair.startDate}-to-${pair.endDate}`);

      // Canonical overlay/message pipeline
      addAreaHighlightOverlaysAndMessages({
        isAdmin,
        datePairs,
        summaryLines,
        futureDates: formattedFutureDates, // Pass the correctly formatted array
        params: { color: highlightColor, label: 'Strategy Score Study' },
        addOverlay,
        barData,
        extendedBarData,
        updateMessagesPanel: ({ summaryLines, futureDates }) => {
          const tabContents = formatMessagesPanelTabs(summaryLines, futureDates);
          if (onRun) {
            const title = `Strategy Score Study`;
            onRun({ tabContents, title });
          }
        },
        onJournalAppend,
      });
      onClose();
    } catch (err) {
      console.error('[StrategyDesignDialog] RUN error:', err);
      const errorMessage = err.response?.data?.error || err.message;
      alert(`Failed to run strategy design study: ${errorMessage}`);
    }
    // onJournalAppend belongs here: it is passed straight through to the overlay helper, so a
    // stale one would write the study's report into a journal the parent has since replaced.
    // Safe to add — handleRun is only ever an onClick, never a dependency of anything else, so
    // recreating it costs nothing.
    // `user` is gone: the body never reads it — only isAdmin, which is derived from it in
    // AuthContext — so it was recreating this callback on every profile change for nothing.
  }, [api, isAdmin, conditions, scoreThreshold, highlightColor, barData, extendedBarData, addOverlay, onRun, onClose, onJournalAppend]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: 22, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{flexGrow: 1, textAlign: 'center', paddingLeft: '48px'}}>Design A New Strategy</span>
        <HelpIcon helpKey="strategyDesignDialog" />
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: 2, minHeight: 820 }}>
        {/* Condition 1: Planet Harmonics */}
        <Box mb={2}>
          <Typography fontWeight="bold" sx={{ mb: 1 }}>Condition of interest: Planet Harmonics</Typography>
          <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            sx={(theme) => ({ borderRadius: 1, p: 1, ...formRowSx('plain')(theme) })}
          >
            <Typography sx={{ fontWeight: 500, mr: 1 }}>Harmonic</Typography>
            <TextField
              size="small"
              type="number"
              value={form1.harmonic}
              onChange={e => {
                let v = e.target.value;
                // Only allow positive integers between 1 and 180
                if (v === '' || (/^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= 180)) {
                  setForm1(f => ({ ...f, harmonic: v === '' ? '' : parseInt(v, 10) }));
                }
              }}
              sx={{ width: 110, mx: 0.5 }}
              inputProps={{ min: 1, max: 180, step: 1 }}
              placeholder="1-180"
            />
            <Typography sx={{ fontWeight: 500, mx: 1 }}>of planet</Typography>
            <FormControl size="small" sx={{ minWidth: 110, mx: 1 }}>
              <InputLabel>Planet</InputLabel>
              <Select value={form1.planet} onChange={e => setForm1(f => ({ ...f, planet: e.target.value }))} label="Planet">
                {PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>
            <Typography sx={{ fontWeight: 500, mx: 1 }}>Starting Offset</Typography>
            <TextField
              size="small"
              type="number"
              value={form1.offset}
              onChange={e => setForm1(f => ({ ...f, offset: e.target.value }))}
              sx={{ width: 110, mx: 0.5 }}
              inputProps={{ min: 0 }}
            />
            <Typography sx={{ fontWeight: 500, mx: 1 }}>and for</Typography>
            <TextField
              size="small"
              type="number"
              value={form1.percent}
              onChange={e => {
                let v = e.target.value;
                if (v === '' || (Number(v) >= 1 && Number(v) <= 100)) setForm1(f => ({ ...f, percent: v }));
              }}
              sx={{ width: 110, mx: 0.5 }}
              inputProps={{ min: 1, max: 100 }}
              placeholder="1-100"
            />
            <Typography sx={{ fontWeight: 500, mx: 1 }}>% of harmonic</Typography>
            <Typography sx={{ fontWeight: 500, mx: 1 }}>Value</Typography>
            <TextField
              size="small"
              type="number"
              value={form1.value}
              onChange={e => setForm1(f => ({ ...f, value: e.target.value }))}
              sx={{ width: 110, mx: 0.5 }}
            />
            <Typography sx={{ fontWeight: 500, mx: 1 }}>and value otherwise is</Typography>
            <TextField
              size="small"
              type="number"
              value={form1.otherwise}
              onChange={e => setForm1(f => ({ ...f, otherwise: e.target.value }))}
              sx={{ width: 110, mx: 0.5 }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                handleAddCondition(1);
                setLastAddedForm1(form1);
              }}
              disabled={!(isFormFilled(form1, ['planet', 'offset', 'harmonic', 'percent', 'value', 'otherwise']) && isFormChanged(form1, lastAddedForm1, ['planet', 'offset', 'harmonic', 'percent', 'value', 'otherwise']))}
              sx={{ ml: 1, borderColor: '#c6aee7', color: '#6a1b9a' }}
            >
              ADD
            </Button>
          </Box>
        </Box>
        {/* Condition 2: Retrograde to direct */}
        <Box mb={2}>
          <Typography fontWeight="bold" sx={{ mb: 1 }}>Condition of interest: Retrograde to direct</Typography>
          <Box display="flex" alignItems="center" gap={1.5} sx={(theme) => ({ borderRadius: 1, p: 1, ...formRowSx('plain')(theme) })}>
            <Typography sx={{ fontWeight: 500, mx: 1 }}>Retrograde to Direct:</Typography>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Planet</InputLabel>
                <Select
                  value={form3.planet}
                  onChange={e => setForm3(f => ({ ...f, planet: e.target.value }))}
                  label="Planet"
                >
                  {PLANETS.filter(p => !['Sun', 'Moon', 'Rahu'].includes(p)).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
            </FormControl>
            <Typography sx={{ fontWeight: 500, mx: 3 }}>Value for condition of interest</Typography>
            <TextField
              size="small"
              type="number"
              value={form3.value}
              onChange={e => setForm3(f => ({ ...f, value: e.target.value }))}
              sx={{ width: 110, mx: 0.5 }}
            />
            <Typography sx={{ fontWeight: 500, mx: 1 }}>; Value otherwise</Typography>
            <TextField
              size="small"
              type="number"
              value={form3.otherwise}
              onChange={e => setForm3(f => ({ ...f, otherwise: e.target.value }))}
              sx={{ width: 110, mx: 0.5 }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                handleAddCondition(3);
                setLastAddedForm3(form3);
              }}
              disabled={!(isFormFilled(form3, ['planet', 'value', 'otherwise']) && isFormChanged(form3, lastAddedForm3, ['planet', 'value', 'otherwise']))}
              sx={{ ml: 1, borderColor: '#c6aee7', color: '#6a1b9a' }}
            >
              Add
            </Button>
          </Box>
        </Box>
        {/* Condition 3: Direct to retrograde */}
        <Box mb={2}>
          <Typography fontWeight="bold" sx={{ mb: 1 }}>Condition of interest: Direct to retrograde</Typography>
          <Box display="flex" alignItems="center" gap={1.5} sx={(theme) => ({ borderRadius: 1, p: 1, ...formRowSx('plain')(theme) })}>
            <Typography sx={{ fontWeight: 500, mx: 1 }}>Direct to Retrograde:</Typography>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Planet</InputLabel>
                <Select
                  value={form4.planet}
                  onChange={e => setForm4(f => ({ ...f, planet: e.target.value }))}
                  label="Planet"
                >
                  {PLANETS.filter(p => !['Sun', 'Moon', 'Rahu'].includes(p)).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
            </FormControl>
            <Typography sx={{ fontWeight: 500, mx: 3 }}>Value for condition of interest</Typography>
            <TextField
              size="small"
              type="number"
              value={form4.value}
              onChange={e => setForm4(f => ({ ...f, value: e.target.value }))}
              sx={{ width: 110, mx: 0.5 }}
            />
            <Typography sx={{ fontWeight: 500, mx: 1 }}>; Value otherwise</Typography>
            <TextField
              size="small"
              type="number"
              value={form4.otherwise}
              onChange={e => setForm4(f => ({ ...f, otherwise: e.target.value }))}
              sx={{ width: 110, mx: 0.5 }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                handleAddCondition(4);
                setLastAddedForm4(form4);
              }}
              disabled={!(isFormFilled(form4, ['planet', 'value', 'otherwise']) && isFormChanged(form4, lastAddedForm4, ['planet', 'value', 'otherwise']))}
              sx={{ ml: 1, borderColor: '#c6aee7', color: '#6a1b9a' }}
            >
              Add
            </Button>
          </Box>
        </Box>
        {/* Condition 2 (Angle between two planets in degree range) */}
        <Box mb={2}>
          <Typography fontWeight="bold" sx={{ mb: 1 }}>Condition of interest: Angle between two planets</Typography>
          <Box display="flex" alignItems="center" gap={1.5} sx={(theme) => ({ borderRadius: 1, p: 1, ...formRowSx('plain')(theme), minHeight: 56 })}>
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel>Planet</InputLabel>
              <Select value={form6.planet1} onChange={e => setForm6(f => ({ ...f, planet1: e.target.value }))} label="Planet">
                {PLANETS.filter(p => p !== form6.planet2).map(p => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography sx={{ fontWeight: 500 }}>And Planet</Typography>
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel>Planet</InputLabel>
              <Select value={form6.planet2} onChange={e => setForm6(f => ({ ...f, planet2: e.target.value }))} label="Planet">
                {PLANETS.filter(p => p !== form6.planet1).map(p => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography sx={{ fontWeight: 500 }}>with distance from</Typography>
            <TextField
              size="small"
              type="number"
              value={form6.from}
              onChange={e => setForm6(f => ({ ...f, from: e.target.value }))}
              sx={{ width: 70, mx: 0.5 }}
              inputProps={{ min: 0, max: 359 }}
            />
            <Typography sx={{ fontWeight: 500 }}>to</Typography>
            <TextField
              size="small"
              type="number"
              value={form6.to}
              onChange={e => setForm6(f => ({ ...f, to: e.target.value }))}
              sx={{ width: 70, mx: 0.5 }}
              inputProps={{ min: 0, max: 359 }}
            />
            <Typography sx={{ fontWeight: 500 }}>Value for condition of interest</Typography>
            <TextField
              size="small"
              type="number"
              value={form6.value}
              onChange={e => setForm6(f => ({ ...f, value: e.target.value }))}
              sx={{ width: 90, mx: 0.5 }}
            />
            <Typography sx={{ fontWeight: 500 }}>, Value otherwise</Typography>
            <TextField
              size="small"
              type="number"
              value={form6.otherwise}
              onChange={e => setForm6(f => ({ ...f, otherwise: e.target.value }))}
              sx={{ width: 90, mx: 0.5 }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                handleAddCondition(2);
                setLastAddedForm6(form6);
              }}
              disabled={!(isFormFilled(form6, ['planet1','planet2','from','to','value','otherwise']) && isFormChanged(form6, lastAddedForm6, ['planet1','planet2','from','to','value','otherwise']) && form6.planet1 !== form6.planet2 && Number(form6.to) <= getMaxAllowedAngle(form6.planet1, form6.planet2))}
              sx={{ ml: 1, borderColor: '#c6aee7', color: '#6a1b9a' }}
            >
              Add
            </Button>
            {form6.planet1 === form6.planet2 && (
              <Typography color="error" sx={{ ml: 2, fontSize: 13 }}>
                Please select two different planets.
              </Typography>
            )}
            {Number(form6.to) > getMaxAllowedAngle(form6.planet1, form6.planet2) && (
              <Typography color="error" sx={{ ml: 2, fontSize: 13 }}>
                Max allowed angle for {form6.planet1}-{form6.planet2} is {getMaxAllowedAngle(form6.planet1, form6.planet2)}°.
              </Typography>
            )}
          </Box>
        </Box>
        {/* Condition 5 (From/To Midpoint) - Two-line layout for clarity */}
        <Box mb={2}>
          <Typography fontWeight="bold" sx={{ mb: 1 }}>Condition of interest: From/To Midpoint</Typography>
          <Box display="flex" flexDirection="column" gap={1} sx={(theme) => ({ borderRadius: 1, p: 1, ...formRowSx('plain')(theme), minHeight: 90 })}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography sx={{ fontWeight: 'bold' }}>From</Typography>
              <FormControl size="small" sx={{ minWidth: 90 }} disabled>
                <InputLabel>Planet</InputLabel>
                <Select value={form5.fromPlanet} onChange={e => setForm5(f => ({ ...f, fromPlanet: e.target.value }))} label="Planet" disabled>
                {PLANETS.filter(p => p !== form5.fromMid1 && p !== form5.fromMid2).map(p => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
                </Select>
              </FormControl>
              <Typography sx={{ fontWeight: 500 }}>Midpoint between</Typography>
              <FormControl size="small" sx={{ minWidth: 90 }} disabled>
                <InputLabel>Planet</InputLabel>
                <Select value={form5.fromMid1} onChange={e => setForm5(f => ({ ...f, fromMid1: e.target.value }))} label="Planet" disabled>
                {PLANETS.filter(p => p !== form5.fromPlanet && p !== form5.fromMid2).map(p => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
                </Select>
              </FormControl>
              <Typography sx={{ fontWeight: 500 }}>And</Typography>
              <FormControl size="small" sx={{ minWidth: 90 }} disabled>
                <InputLabel>Planet</InputLabel>
                <Select value={form5.fromMid2} onChange={e => setForm5(f => ({ ...f, fromMid2: e.target.value }))} label="Planet" disabled>
                {PLANETS.filter(p => p !== form5.fromPlanet && p !== form5.fromMid1).map(p => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
                </Select>
              </FormControl>
              <Box sx={{ flexGrow: 1 }} />
              <Typography sx={{ fontWeight: 'bold', ml: 3 }}>To</Typography>
              <Typography sx={{ fontWeight: 500, ml: 1 }}>Midpoint between</Typography>
              <FormControl size="small" sx={{ minWidth: 90 }} disabled>
                <InputLabel>Planet</InputLabel>
                <Select value={form5.toMid1} onChange={e => setForm5(f => ({ ...f, toMid1: e.target.value }))} label="Planet">
                {PLANETS.filter(p => p !== form5.toPlanet && p !== form5.toMid2).map(p => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
                </Select>
              </FormControl>
              <Typography sx={{ fontWeight: 500 }}>And</Typography>
              <FormControl size="small" sx={{ minWidth: 90 }} disabled>
                <InputLabel>Planet</InputLabel>
                <Select value={form5.toMid2} onChange={e => setForm5(f => ({ ...f, toMid2: e.target.value }))} label="Planet" disabled>
                {PLANETS.filter(p => p !== form5.toPlanet && p !== form5.toMid1).map(p => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 90 }} disabled>
                <InputLabel>Planet</InputLabel>
                <Select value={form5.toPlanet} onChange={e => setForm5(f => ({ ...f, toPlanet: e.target.value }))} label="Planet" disabled>
                {PLANETS.filter(p => p !== form5.toMid1 && p !== form5.toMid2).map(p => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
                </Select>
              </FormControl>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography sx={{ fontWeight: 500 }}>Value for condition of interest</Typography>
              <TextField
                size="small"
                type="number"
                value={form5.value}
                onChange={e => setForm5(f => ({ ...f, value: e.target.value }))}
                sx={{ width: 110, mx: 0.5 }}
                disabled
              />
              <Typography sx={{ fontWeight: 500 }}>; Value otherwise</Typography>
              <TextField
                size="small"
                type="number"
                value={form5.otherwise}
                onChange={e => setForm5(f => ({ ...f, otherwise: e.target.value }))}
                sx={{ width: 110, mx: 0.5 }}
                disabled
              />
              <Button
                variant="outlined"
                onClick={() => handleAddCondition(5)}
                disabled
                sx={{ ml: 1, borderColor: '#c6aee7', color: '#6a1b9a' }}
              >
                Add
              </Button>
            </Box>
          </Box>
        </Box>
        {/* Selected Strategy Components List */}
        <Box display="flex" gap={3}>
          {/* Left: List and label */}
          <Box sx={{ width: '70%' }}>
            <Typography fontWeight="bold">Selected Strategy Components</Typography>
            <List sx={(theme) => ({ minHeight: 240, maxHeight: 320, overflow: 'auto', ...formRowSx('plain')(theme) })}>
              {conditions.length === 0 ? (
                <ListItem>
                  <ListItemText primary={<Typography color="textSecondary">No components added</Typography>} />
                </ListItem>
              ) : (
                conditions.map((cond, idx) => (
                  <ListItem
                    key={idx}
                    selected={selectedIndex === idx}
                    onClick={() => setSelectedIndex(idx)}
                  >
                    <ListItemText primary={renderConditionSummary(cond)} />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(idx)}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))
              )}
            </List>
            {/* Bottom row: Close (left), Run (right) */}
            <Box display="flex" direction="row" justifyContent="space-between" alignItems="center" mt={1}>
              <Button onClick={onClose} variant="outlined">Close</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleRun}
                disabled={conditions.length === 0 || !scoreThreshold}
              >
                RUN
              </Button>
            </Box>
          </Box>
          {/* Right: Color picker */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', ml: 2 }}>
            <Typography fontWeight="bold" mb={1} sx={{ alignSelf: 'flex-start' }}>Select Highlight Color</Typography>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={highlightColor}
                onChange={e => setHighlightColor(e.target.value)}
                renderValue={color => (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 24, height: 18, bgcolor: color, border: '1px solid #888', mr: 1 }} />
                    <span>{color}</span>
                  </Box>
                )}
              >
                {[
                  'rgba(33,150,243,0.7)',
                  'rgba(244,67,54,0.5)',
                  'rgba(76,175,80,0.5)',
                  'rgba(255,235,59,0.5)',
                  'rgba(156,39,176,0.5)',
                  'rgba(255,152,0,0.5)',
                  'rgba(121,85,72,0.5)',
                  'rgba(0,188,212,0.5)',
                  'rgba(205,220,57,0.5)',
                  'rgba(233,30,99,0.5)',
                  'rgba(158,158,158,0.5)',
                  'rgba(0,0,0,0.3)'
                ].map(color => (
                  <MenuItem key={color} value={color}>
                    <Box sx={{ width: 24, height: 18, bgcolor: color, border: '1px solid #888', mr: 1, display: 'inline-block' }} />
                    <span>{color}</span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box mt={3} display="flex" alignItems="center" gap={1}>
              <Typography>Highlight Area when total strategy score is equal to or more than</Typography>
              <TextField
                size="small"
                type="number"
                sx={{ width: 110 }}
                value={scoreThreshold}
                onChange={e => setScoreThreshold(e.target.value.replace(/[^\d.-]/g, ''))}
                inputProps={{ min: 0 }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

StrategyDesignDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired,
  barData: PropTypes.object,
  extendedBarData: PropTypes.object.isRequired,
  onJournalAppend: PropTypes.func,
  /** A Command Bus prefill. Applied once per distinct object; null means "no prefill". */
  initialConfig: PropTypes.object,
};
